import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, Trash2 } from 'lucide-react';
import { Webhook } from '@/services/webhookService';

interface DeleteWebhookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  webhook: Webhook | null;
  onConfirm: () => Promise<void>;
}

const DeleteWebhookDialog: React.FC<DeleteWebhookDialogProps> = ({
  open,
  onOpenChange,
  webhook,
  onConfirm,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  if (!webhook) return null;

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      await onConfirm();
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-red-600" />
            </div>
            Delete Webhook
          </AlertDialogTitle>
          <AlertDialogDescription className="pt-2">
            Are you sure you want to delete this webhook? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="py-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2">
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1">Name</p>
              <p className="text-sm font-semibold text-slate-900">
                {webhook.name || 'Unnamed Webhook'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1">URL</p>
              <p className="text-sm text-slate-900 font-mono break-all">
                {webhook.webhook_url}
              </p>
            </div>
          </div>
          <p className="text-sm text-slate-600 mt-4">
            This webhook will stop receiving event notifications immediately. All associated
            configuration and delivery history will be permanently deleted.
          </p>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Webhook
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteWebhookDialog;

